import { useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { CloudUpload, Music, Loader, Trash2 } from 'lucide-react';
import { VideoCamera } from '@phosphor-icons/react';
import { useMediaLibraryStore, type LibraryAsset } from '@/features/editor/store/mediaLibraryStore';

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, '0');
  const s = (totalSec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ── Media Card ──

const MediaCard = memo(function MediaCard({ asset }: { asset: LibraryAsset }) {
  const isAudio = asset.mediaType === 'audio' || asset.mimeType?.startsWith('audio/');
  const isReady = asset.ingestStatus === 'ready';
  const isIngesting = asset.ingestStatus === 'uploading' || asset.ingestStatus === 'pending' || asset.ingestStatus === 'generating-proxy';
  const removeAsset = useMediaLibraryStore((s) => s.removeAsset);

  return (
    <div className="rounded-xl overflow-hidden bg-[#1A1A1E] border border-white/5 hover:border-white/15 transition-all group">
      {/* Thumbnail */}
      <div className="relative aspect-video bg-[#2A2A2E]">
        {isIngesting ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Loader size={24} className="animate-spin text-[var(--user-page-accent)]" />
            {asset.ingestProgress > 0 && (
              <div className="w-1/2 h-1 bg-black/30 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--user-page-accent)] transition-all" style={{ width: `${asset.ingestProgress}%` }} />
              </div>
            )}
          </div>
        ) : isAudio ? (
          <div className="w-full h-full flex items-center justify-center">
            <Music size={32} className="text-[var(--user-page-accent)] opacity-50" />
          </div>
        ) : asset.thumbnailUrl ? (
          <img src={asset.thumbnailUrl} alt={asset.filename} className="w-full h-full object-contain" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <VideoCamera size={32} className="text-zinc-600" />
          </div>
        )}

        {/* Duration pill */}
        {asset.durationMs > 0 && isReady && (
          <div className="absolute bottom-2 start-2 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded-md border border-white/10">
            <span className="text-[11px] font-medium text-white tabular-nums tracking-wider">
              {formatDuration(asset.durationMs)}
            </span>
          </div>
        )}

        {/* Delete button on hover */}
        <button
          onClick={() => removeAsset(asset.id)}
          className="absolute top-2 end-2 p-1.5 rounded-lg bg-black/60 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
        >
          <Trash2 size={14} />
        </button>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors pointer-events-none" />
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-[var(--user-page-text)] truncate">{asset.filename}</h3>
        <p className="text-xs text-zinc-500 mt-0.5">
          {asset.width > 0 && asset.height > 0 ? `${asset.width}×${asset.height}` : ''}
          {asset.width > 0 && asset.durationMs > 0 ? ' · ' : ''}
          {asset.durationMs > 0 ? formatDuration(asset.durationMs) : ''}
        </p>
      </div>
    </div>
  );
});

// ── Page ──

export function VideosPage() {
  const { t } = useTranslation();
  const assets = useMediaLibraryStore((s) => s.assets);
  const loadFromBackend = useMediaLibraryStore((s) => s.loadFromBackend);
  const uploadFile = useMediaLibraryStore((s) => s.uploadFile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    for (const file of files) uploadFile(file);
    e.target.value = '';
  }, [uploadFile]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-5 px-4 sm:px-6 md:px-8"
    >
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-2xl font-semibold">{t('videos.title', 'My Media')}</h1>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--user-page-accent)] px-3 py-1.5 text-sm font-medium text-[var(--user-page-bg)] hover:brightness-110 transition-all"
        >
          <CloudUpload size={16} />
          {t('videos.upload', 'Upload')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,audio/*"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <VideoCamera size={32} className="text-zinc-600 mb-3" />
          <p className="text-zinc-500">{t('videos.empty', 'No media yet')}</p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {assets.map((asset) => <MediaCard key={asset.id} asset={asset} />)}
        </div>
      )}
    </motion.div>
  );
}
